import d3 from '@/plugins/d3';
import planningService from '../services/admin';
import { make } from 'vuex-pathify';
import {
  orderBy,
  concat,
  groupBy,
  isEqual,
  defaultsDeep,
  findIndex,
  flatMap,
  uniq,
  pickBy,
  forEach,
} from 'lodash';
import {
  formatISO,
  startOfDay,
  endOfDay,
  isSameDay,
  parseISO,
  isBefore,
} from 'date-fns';
import handleErrors from '@/functions/handleErrors';

// TODO: split this store into separate CONTAINED modules.
// Timeline helper functions should not interfere with data store, dialog state, or header state.

const state = {
  dataLoading: true,
  planningInterval: 'day', // Header - planningViewState
  planningType: 'jobs', // Header - planningViewState
  dateStart: new Date(), // Header
  dateEnd: new Date(), // Header
  dates: [], // Header - all days between dateStart and dateEnd inclusive - planningViewState
  errors: [],
  plannedJobFilters: {},
  assignmentFilters: {},
  planningAssignmentFilters: {},
  activeObject: undefined, // sidePanel Focus object - planningViewState
  margins: {
    // Timeline
    top: 0,
    right: 0,
    bottom: 0,
    left: 20,
  },
  width: 1500, // Timeline
  rowHeight: 64, // Timeline
  rowLimit: 100, // Timeline

  planningAssignments: [], // Data
  plannedExtraBlocks: [], // Data - all auxiliary planned timeslots in planning (not jobs or assignments)
  plannedScheduleBlocks: [], // Data / Timeline - schedules per resource
  plannedJobs: [], // Data - all jobs, with and without assignments
  userWorkShifts: [], // Data - schedules per resource
  machineSchedules: [], // Data - schedules per resource
  overlappingAssignmentIds: [], // Data - ids of assignments conflicting with other timeslots

  resources: [], // resourcesMenu content
  resourceQuery: '', // resourcesMenu textinput filter
  mainResource: 'employee', // resourcesMenu type select value - planningViewState
  mainResourceTypes: ['employee', 'external_employee', 'machine', 'job'], // resourcesMenu type select options
  showWorkShifts: false,
  activeResourceFilters: {
    employee: {},
    external_employee: {},
    machine: {},
    job: {},
  },
  activeResourceSorts: {
    employee: 'lastName',
    external_employee: undefined,
    machine: undefined,
    job: 'orderedByProjectCode',
  },
  resourceSortOptions: {
    employee: ['lastName', '-lastName', 'firstName', '-firstName'],
    job: ['orderedByProjectCode'],
  },
  showPlannedJobDropzone: false, // jobPlanningAxis
  showDeleteBin: false, // jobPlanningAxis
  highlighted: undefined, // jobPlanningAxis / jobPlanningMenu
  showAvailableUserSecondsRelative: true,
};

const getters = {
  // Gridlines
  majorTickCount: (state, getters) => {
    if (getters.isDay) return Math.floor(Math.min(24, state.width / 80)); // 24 hours, at least 80px wide
    return state.dates.length;
  },
  minorTickCount: (state, getters) => {
    return getters.majorTickCount * 4;
  },
  majorTicks: (state, getters) => {
    return getters.scaleX.ticks(getters.majorTickCount);
  },
  minorTicks: (state, getters) => {
    return getters.scaleX.ticks(getters.minorTickCount);
  },
  scaleX: (state, getters) => {
    return d3
      .scaleTime()
      .domain([getters.minTime, getters.maxTime])
      .range([getters.gridEdge.left, getters.gridEdge.right])
      .nice();
  },
  scaleY: (state, getters) => {
    return d3
      .scaleBand()
      .domain(getters.rowKeys)
      .range([getters.gridEdge.top, getters.gridEdge.bottom])
      .round(true)
      .align(0.5);
  },
  gridEdge: (state, getters) => {
    return {
      left: state.margins.left,
      top: state.margins.top,
      right: state.width - state.margins.right,
      bottom: getters.height - state.margins.bottom,
    };
  },
  timezone: state => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  },
  minTime: state => {
    return startOfDay(state.dateStart);
  },
  maxTime: state => {
    return endOfDay(state.dateEnd);
  },
  height: (state, getters) => {
    return (
      state.margins.top +
      state.margins.bottom +
      getters.rowKeys.length * state.rowHeight
    );
  },
  rowHeight: (state, getters) => {
    return getters.scaleY.bandwidth();
  },
  // Data
  filteredResources: state => {
    var searchString = state.resourceQuery.toLowerCase();
    //by default show all results if search text field is empty
    if (!(searchString && searchString != '')) {
      return state.resources;
    }
    /* 
      if we are on 'timeline', then all results can be done with normal filtering, 
      otherwise on 'jobs', only 'employee' and 'jobs' can be done with normal filtering.
      'ExternalEmployee' and 'Machine' are one level deeper than 'employee' and 'jobs' in 'jobs' planning type
    */
    if (
      state.planningType == 'timeline' ||
      state.mainResource == 'employee' ||
      state.mainResource == 'job'
    ) {
      return state.resources.filter(record =>
        record.name.toLowerCase().includes(searchString)
      );
    } else if (
      state.mainResource == 'external_employee' ||
      state.mainResource == 'machine'
    ) {
      var attribute_name =
        state.mainResource == 'external_employee'
          ? 'availableExternalEmployees'
          : 'machines';

      var resultArr = [];
      for (var i = 0; i < state.resources.length; i++) {
        const results = state.resources[i][attribute_name]?.filter(record =>
          record.name.toLowerCase().includes(searchString)
        );
        if (results.length > 0) resultArr.push(JSON.parse(JSON.stringify(state.resources[i])))

        //return object cointaining arrays of results
        for (var k = 0; k < resultArr.length; k++) {
          resultArr[k][attribute_name] = resultArr[k][attribute_name]?.filter(record =>
            record.name.toLowerCase().includes(searchString)
          );
        }
      }
      return resultArr;
    } else {
      return state.resources;
    }
  },
  sortedPlanningBlocks: state => {
    return orderBy(
      concat(
        state.planningAssignments,
        state.plannedJobs,
        state.plannedExtraBlocks
      ) || [], // split into two for separate rows
      ['startTime', 'endTime'], // needs timeEnd and timeStart at extra blocks' resources
      ['asc', 'desc']
    );
  },
  plannedJobs: state => {
    return state.plannedJobs;
  },
  planningResourceBlocks: (state, getters) => {
    let groupedBlocks = groupBy(getters.sortedPlanningBlocks, block => {
      if (block.type === 'planningAssignments') {
        return block.assignableId;
      } else if (block.type === 'plannedJobs') {
        return block.jobId;
      } else if (block.type === 'rentalLines') {
        return block.machineId;
      } else {
        return block.userId;
      }
    });
    return new Map(Object.entries(groupedBlocks)); // Map so it's iterable
  },
  planningResourceRows: (state, getters) => {
    // TODO: Sort here, accept parameter with blocks to be turned into rows instead of being a getter
    let result = [];

    // THIS FUNCTION ASSUMES SORTED ARRAY BY STARTTIME
    // Loop through reverse copy of blocks for faster array mutation (minimize index shifts)

    for (const [key, blocks] of getters.planningResourceBlocks) {
      let blocksReverseCopy = blocks.slice().reverse();

      const addRows = (arr, i = 0) => {
        // Take mutated array to recursively loop through remaining timeline blocks
        // use custom iterator for row indices
        let row = arr.reduceRight((acc, block, index) => {
          const currentStart = parseISO(block.startTime); //timeStart & timeEnd
          const lastEnd = !acc?.length
            ? new Date(0)
            : acc[acc.length - 1]?.endTime
            ? parseISO(acc[acc.length - 1]?.endTime)
            : getters.maxTime; // if endTime is null, fill entire row.

          let blockOverlaps = isBefore(currentStart, lastEnd);

          if (!getters.isDay) {
            blockOverlaps = blockOverlaps || isSameDay(currentStart, lastEnd);
          }

          if (!blockOverlaps) {
            acc.push(block);
            arr.splice(index, 1);
          }

          return acc;
        }, []); // initial value empty array

        // Push custom object for adding indeterminate rows to scaleY
        result.push({
          resourceId: key,
          rowIndex: i,
          rowKey: `${key}_${i}`,
          blocks: row,
        });

        // Final case, all items in arr used
        if (arr.length <= 0) return;

        // Repeat until final case is reached
        i += 1;
        addRows(arr, i);
      };
      addRows(blocksReverseCopy);
    }
    const filteredResourceIds = getters.filteredResources.map(x => x.id);
    return result.filter(x => filteredResourceIds.includes(x.resourceId));
  },
  resourcesWithoutBlocks: (state, getters) => {
    const resourceIds = Object.values(getters.planningResourceRows).map(
      x => x.resourceId
    );
    return getters.filteredResources.filter(x => !resourceIds.includes(x.id));
  },
  rowKeys: (state, getters) => {
    let keys = Object.values(getters.planningResourceRows).map(x => x.rowKey);
    const extraKeys = getters.resourcesWithoutBlocks.map(x => `${x.id}_0`);
    return keys.concat(extraKeys);
  },
  // View helpers
  isDay: state => {
    return state.planningInterval === 'day';
  },
  isWeek: state => {
    return state.planningInterval === 'week';
  },
  isMonth: state => {
    return state.planningInterval === 'month';
  },
  activeFilters: state => {
    const filters = state.activeResourceFilters?.[state.mainResource] || {};
    return pickBy(filters, (v, _k) => v?.length > 0);
  },
  activeResourceSortOptions: state => {
    return state.resourceSortOptions?.[state.mainResource] || [];
  },
  activeSort: state => {
    return state.activeResourceSorts?.[state.mainResource];
  },
};

const mutations = {
  ...make.mutations(state),
  SET_SHOW_WORK_SHIFTS(state, boolean) {
    state.showWorkShifts = boolean;
  },
  SET_LOADING_STATE(state, loading) {
    state.dataLoading = loading;
  },
  interval(state, interval) {
    // interval in time between start and end (as per date-fns naming), planningInterval is string representation of selected 'interval' in dateselector
    state.dateStart = interval.start;
    state.dateEnd = interval.end;
  },
  dates(state, dates) {
    state.dates = dates;
  },
  SET_RESOURCE_QUERY(state, query) {
    state.resourceQuery = query;
  },
  SET_WIDTH(state, width) {
    state.width = width;
  },
  ADD_RESOURCES(state, resources) {
    state.resources = [...state.resources, ...resources];
  },
  RESET_RESOURCES(state) {
    state.resources = [];
    state.userWorkShifts = [];
    state.machineSchedules = [];
  },
  RESET_PLANNED_EXTRA_BLOCKS(state) {
    state.plannedExtraBlocks = [];
  },
  RESET_PLANNED_SCHEDULE_BLOCKS(state) {
    state.plannedScheduleBlocks = [];
  },
  RESET_BLOCKS(state) {
    state.planningAssignments = [];
    state.plannedExtraBlocks = [];
    state.plannedJobs = [];
    state.userWorkShifts = [];
    state.machineSchedules = [];
  },
  SET_RESOURCE_FILTERS(state, filters) {
    state.activeResourceFilters[state.mainResource] = filters;
  },
  SET_ALL_RESOURCE_FILTERS(state, filters) {
    forEach(state.mainResourceTypes, function(x) {
      state.activeResourceFilters[x] = filters;
    });
  },
  SET_RESOURCE_SORT(state, sort) {
    state.activeResourceSorts[state.mainResource] = sort;
  },
  SET_ACTIVE_OBJECT(state, planningBlock) {
    state.activeObject = planningBlock;
  },
  ADD_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_PLANNING_TYPE(state, planningType) {
    state.planningType = planningType;
  },
  SET_PLANNED_JOB_RELOADING(state, id) {
    const index = findIndex(state.plannedJobs, p => p.id === id);
    if (index < 0) return;
    Object.assign(state.plannedJobs[index], { reloading: true });
  },
  REFRESH_PLANNED_JOB(state, plannedJob) {
    const index = findIndex(state.plannedJobs, p => p.id === plannedJob.id);
    if (index < 0) return;
    Object.assign(state.plannedJobs[index], {
      ...plannedJob,
      reloading: false,
    });
  },
  REFRESH_PLANNING_ASSIGNMENT(state, planningAssignment) {
    const index = findIndex(
      state.planningAssignments,
      p => p.id === planningAssignment.id
    );
    if (index < 0) return;
    Object.assign(state.planningAssignments[index], planningAssignment);
  },
  REFRESH_USER_WORK_SHIFT(state, userWorkShift) {
    const index = findIndex(
      state.userWorkShifts,
      p => p.userId === userWorkShift.userId
    );
    if (index < 0) return;
    Object.assign(state.userWorkShifts[index], userWorkShift);
  },
  REFRESH_MACHINE_SCHEDULE(state, machineSchedule) {
    const index = findIndex(
      state.machineSchedules,
      p => p.machineId === machineSchedule.machineId
    );
    if (index < 0) return;
    Object.assign(state.machineSchedules[index], machineSchedule);
  },
  REMOVE_PLANNING_ASSIGNMENT(state, id) {
    const index = findIndex(state.planningAssignments, i => i.id === id);
    if (index < 0) return;
    state.planningAssignments.splice(index, 1);
  },
  REMOVE_PLANNED_JOB(state, id) {
    const index = findIndex(state.plannedJobs, i => i.id === id);
    if (index < 0) return;
    state.plannedJobs.splice(index, 1);
  },
  ADD_PLANNED_JOB(state, plannedJob) {
    state.plannedJobs.push(plannedJob);
  },
  ADD_PLANNING_ASSIGNMENT(state, planningAssignment) {
    state.planningAssignments.push(planningAssignment);
  },
  ADD_OVERLAPPING_ASSIGNMENT_IDS(state, overlappingAssignmentIds) {
    const ids = state.overlappingAssignmentIds.concat(overlappingAssignmentIds);
    state.overlappingAssignmentIds = uniq(ids);
  },
  RESET_OVERLAPPING_ASSIGNMENT_IDS(state) {
    state.overlappingAssignmentIds = [];
  },
  SET_SHOW_AVAILABLE_USER_SECONDS_RELATIVE(state, boolean) {
    state.showAvailableUserSecondsRelative = boolean;
  },
  SET_PLANNED_JOB_FILTERS(state, filters={}) {
    state.plannedJobFilters = filters;
  },
  SET_ASSIGNMENT_FILTERS(state, filters={}) {
    state.assignmentFilters = filters;
  },
};

const actions = {
  // REFACTOR: Use import { make } from 'vuex-pathify' and ...make.actions() to instantly create basic actions for every mutation.
  toggleShowWorkShifts({ commit, state }) {
    commit('SET_SHOW_WORK_SHIFTS', !state?.showWorkShifts);
  },
  addErrors({ commit }, errors) {
    commit('ADD_ERRORS', errors);
  },
  setMainResource({ commit }, type) {
    commit('SET_ACTIVE_OBJECT', null);
    commit('mainResource', type);
  },
  setPlanningType({ commit }, planningType) {
    commit('SET_PLANNING_TYPE', planningType);
  },
  setWidth({ commit }, width) {
    commit('SET_WIDTH', width);
  },
  addResources({ commit }, resources) {
    commit('ADD_RESOURCES', resources);
  },
  setplanningAssignments({ commit }, resources) {
    commit('planningAssignments', resources);
  },
  setPlanningReparations({ commit }, resources) {
    commit('SET_PLANNING_REPARATIONS', resources);
  },
  setResourceQuery({ commit }, query) {
    commit('SET_RESOURCE_QUERY', query);
  },
  setResourceFilters({ commit }, filters) {
    commit('SET_RESOURCE_FILTERS', filters);
  },
  setResourceSort({ commit }, sort) {
    commit('SET_RESOURCE_SORT', sort);
  },
  setResourcesMachines({ dispatch }) {
    dispatch('setResources', {
      serviceAction: planningService.getAdminMachines,
    });
  },
  setResourcesItems({ dispatch, getters }) {
    dispatch('setResources', {
      serviceAction: planningService.getAdminItems,
      params: {
        filter: {
          ...getters.activeFilters,
        },
      },
    });
  },
  setResourcesJobs({ dispatch, getters }) {
    dispatch('setResources', {
      serviceAction: planningService.getAdminJobs,
      params: {
        sort: getters.activeSort || '',
        filter: {
          after_start: formatISO(getters.minTime),
          before_end: formatISO(getters.maxTime),
          ...getters.activeFilters,
        },
      },
    });
  },
  setResourcesExternalEmployees({ state, dispatch, getters }) {
    if (state.planningType === 'jobs') {
      dispatch('setResources', {
        serviceAction: planningService.getAdminEmploymentAgencies,
        params: {
          filter: {
            ...getters.activeFilters,
          },
        },
      });
    } else {
      dispatch('setResources', {
        serviceAction: planningService.getAdminExternalEmployees,
        params: {
          filter: {
            blocked_from_date_after: formatISO(getters.minTime),
            ...getters.activeFilters,
          },
        },
      });
    }
  },
  setResourcesInternalEmployees({ dispatch, getters }) {
    dispatch('setResources', {
      serviceAction: planningService.getAdminInternalEmployees,
      params: {
        sort: getters.activeSort || '',
        filter: {
          with_contract_end_after: formatISO(getters.minTime),
          with_contract_start_before: formatISO(getters.maxTime),
          ...getters.activeFilters,
        },
      },
    });
  },
  setPlannedJobReloading({ commit }, id) {
    commit('SET_PLANNED_JOB_RELOADING', id);
  },
  async refreshPlannedJob({ dispatch, commit }, id) {
    const plannedJob = await dispatch('getPlannedJob', id);
    await commit('REFRESH_PLANNED_JOB', plannedJob);
  },
  async refreshPlanningAssignment({ dispatch, commit }, id) {
    const planningAssignment = await dispatch('getPlanningAssignment', id);
    commit('REFRESH_PLANNING_ASSIGNMENT', planningAssignment);
  },
  async refreshAssignmentsForJob({ dispatch, commit }, jobId) {
    const assignments = state.planningAssignments.filter(
      assignment => assignment.plannedJobId == jobId
    );
    await Promise.all(
      assignments.map(async assignment => {
        const planningAssignment = await dispatch(
          'getPlanningAssignment',
          assignment.id
        );
        commit('REFRESH_PLANNING_ASSIGNMENT', planningAssignment);
      })
    );
  },
  async refreshUserWorkShift({ dispatch, commit }, userId) {
    const userWorkShift = await dispatch('getUserWorkShift', userId);
    commit('REFRESH_USER_WORK_SHIFT', userWorkShift);
  },
  async refreshMachineSchedule({ dispatch, commit }, machineId) {
    const machineSchedule = await dispatch('getMachineSchedule', machineId);
    commit('REFRESH_MACHINE_SCHEDULE', machineSchedule);
  },
  async getPlanningAssignments({ dispatch, getters }) {
    let filters = {
      end_after: formatISO(getters.minTime),
      start_before: formatISO(getters.maxTime),
      user_assignments: true,
      machine_assignments: true,
    };
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlanningAssignments,
      params: { filter: filters},
    });
  },
  async getPlannedUserAssignments({ state, dispatch, getters }) {
    let filters = {
      end_after: formatISO(getters.minTime),
      start_before: formatISO(getters.maxTime),
      user_assignments: true,
      ...state.assignmentFilters
    };
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlanningAssignments,
      params: { filter: filters },
    });
  },
  async getPlannedMachineAssignments({ state, dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlanningAssignments,
      params: {
        filter: {
          end_after: formatISO(getters.minTime),
          start_before: formatISO(getters.maxTime),
          machine_assignments: true,
          ...state.assignmentFilters
        },
      },
    });
  },
  async getLeaveRequests({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminLeaveRequests,
      params: {
        filter: {
          end_after: formatISO(getters.minTime),
          start_before: formatISO(getters.maxTime),
        },
      },
    });
  },
  async getSickRegistrations({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminSickRegistrations,
      params: {
        filter: {
          end_after: formatISO(getters.minTime),
          start_before: formatISO(getters.maxTime),
        },
      },
    });
  },
  async getPlannedJobs({ state, dispatch, getters }) {
    let filters = {
      end_after: formatISO(getters.minTime),
      start_before: formatISO(getters.maxTime),
      ...state.plannedJobFilters,
    };
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlannedJobs,
      params: { filter: filters },
    });
  },
  async getPlannedJob(_, id) {
    try {
      const { data } = await planningService.getAdminPlannedJob(id);
      return data;
    } catch (e) {
      throw handleErrors(e);
    }
  },
  async getPlanningAssignment(_, id) {
    try {
      const { data } = await planningService.getAdminPlanningAssignment(id);
      return data;
    } catch (e) {
      throw handleErrors(e);
    }
  },
  async getRentalLines({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminRentalLines,
      params: {
        filter: {
          end_after: formatISO(getters.minTime),
          start_before: formatISO(getters.maxTime),
        },
      },
    });
  },
  async getReparations({ dispatch }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminReparations,
      params: {
        filter: {
          open: true,
        },
      },
    });
  },
  async getPlanningWorkShiftBlocks({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlanningWorkShiftBlocks,
      params: {
        filter: {
          time_start: formatISO(getters.minTime),
          time_end: formatISO(getters.maxTime),
        },
      },
    });
  },
  async getPlanningJobScheduleBlocks({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminPlanningJobScheduleBlocks,
      params: {
        filter: {
          time_start: formatISO(getters.minTime),
          time_end: formatISO(getters.maxTime),
        },
      },
    });
  },
  async getUserWorkShift({ getters }, userId) {
    const { data } = await planningService.getAdminUserWorkShift(userId, {
      filter: {
        with_date_start: formatISO(getters.minTime),
        with_date_end: formatISO(getters.maxTime),
        timezone: getters.timezone,
      },
    });
    return data;
  },
  async getUserWorkShifts({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminUserWorkShifts,
      params: {
        filter: {
          with_date_start: formatISO(getters.minTime),
          with_date_end: formatISO(getters.maxTime),
          timezone: getters.timezone,
        },
        page: {
          limit: 100,
        },
      },
    });
  },
  async getUserMissingCertificateTypes({ commit, getters }, props = {}) {
    const { data } = await planningService.getAdminMissingCertificateTypes({
      filter: {
        user_id: props.userId,
        user_function_id: props.userFunctionId,
        with_time_start: formatISO(getters.minTime),
        with_time_end: formatISO(getters.maxTime),
      },
    });
    return data;
  },
  async getMachineSchedule({ getters }, machineId) {
    const { data } = await planningService.getAdminMachineSchedule(machineId, {
      filter: {
        with_date_start: formatISO(getters.minTime),
        with_date_end: formatISO(getters.maxTime),
        timezone: getters.timezone,
      },
    });
    return data;
  },
  async getMachineSchedules({ dispatch, getters }) {
    return await dispatch('getAllResults', {
      serviceAction: planningService.getAdminMachineSchedules,
      params: {
        filter: {
          with_date_start: formatISO(getters.minTime),
          with_date_end: formatISO(getters.maxTime),
          timezone: getters.timezone,
        },
        page: {
          limit: 100,
        },
      },
    });
  },
  async setResources({ state, dispatch, commit }, payload) {
    defaultsDeep(payload, { params: { page: { limit: state.rowLimit } } });
    commit('resources', await dispatch('getAllResults', payload));
  },
  async getAllResults({ commit }, { serviceAction, params }) {
    // only 1 payload is supported; here it's immediately destructured
    // This action doesn't mutate state, it only returns results. Could be a separate function.
    const resultLimit = 500; // Limit the results to 500
    let results = [];
    params = params || {};
    let offset = params?.page?.offset || 0;

    async function apiCall(offset) {
      let internalParams = { page: { offset } };
      const newParams = defaultsDeep(internalParams, params, { page: { limit: 100 } });
      try {
        const { data, meta } = await serviceAction(newParams);
        results = results.concat(data);
        if (meta.recordCount > results.length && results.length < resultLimit) {
          await apiCall(offset + data.length); //recursive trigger
        }
      } catch (error) {
        console.log(error);
      }
    }
    await apiCall(offset);
    return results;
  },
  async postPlannedJob({ dispatch }, jobData) {
    try {
      const { data } = await planningService.postAdminPlannedJob({
        jobId: jobData.jobId,
        date: jobData.date,
      });
      // await dispatch('addPlannedJob', data.id);
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async addPlannedJob({ commit }, plannedJobId) {
    const plannedJob = await planningService.getAdminPlannedJob(plannedJobId);
    commit('ADD_PLANNED_JOB', plannedJob.data);
  },
  async addPlanningAssignment({ commit }, id) {
    const planningAssignment = await planningService.getAdminPlanningAssignment(
      id
    );
    commit('ADD_PLANNING_ASSIGNMENT', planningAssignment.data);
  },
  async postAssignment({ dispatch }, formData) {
    try {
      dispatch('setPlannedJobReloading', formData?.plannedJobId);
      const { data } = await planningService.postAdminPlanningAssignment(
        formData
      );
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async patchAssignment({ dispatch }, formData) {
    try {
      dispatch('setPlannedJobReloading', formData?.plannedJobId);
      const { data } = await planningService.patchAdminPlanningAssignment(
        formData.id,
        formData
      );
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async deleteAssignment({ commit, dispatch }, options) {
    try {
      dispatch('setPlannedJobReloading', options?.plannedJobId);
      await planningService.deleteAdminPlanningAssignment(options.id);
      commit('REMOVE_PLANNING_ASSIGNMENT', options.id);
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async patchPlannedJob({ dispatch }, formData) {
    try {
      dispatch('setPlannedJobReloading', formData?.id);
      const { data } = await planningService.patchAdminPlannedJob(
        formData.id,
        formData
      );
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async destroyPlannedJob({ dispatch }, formData) {
    try {
      await planningService.patchAdminPlannedJob(formData.id, formData);
    } catch (e) {
      dispatch('addErrors', handleErrors(e));
    }
  },
  async removePlannedJob({ commit }, jobId) {
    await commit('REMOVE_PLANNED_JOB', jobId);
  },
  async removePlanningAssignment({ commit }, id) {
    await commit('REMOVE_PLANNING_ASSIGNMENT', id);
  },
  async plannedJobCreateStockOrder({ commit, dispatch }, id) {
    commit('SET_LOADING_STATE', true);
    await dispatch('patchPlannedJob', {
      id: id,
      createStockOrder: true,
    });
    commit('SET_LOADING_STATE', false);
  },
  async plannedJobPublish({ dispatch }, id) {
    await dispatch('patchPlannedJob', {
      id: id,
      published: true,
      sendEmail: true,
    });
  },
  async plannedJobDepublish({ dispatch }, id) {
    await dispatch('patchPlannedJob', { id: id, published: false });
  },
  async initialize(
    { state, commit, dispatch },
    { refreshResources = true, refreshBlocks = true } = {}
  ) {
    commit('SET_LOADING_STATE', true);
    if (refreshBlocks) {
      commit('RESET_BLOCKS');
      commit('RESET_PLANNED_EXTRA_BLOCKS');
      commit('RESET_PLANNED_SCHEDULE_BLOCKS');
    }
    if (refreshResources) {
      await commit('RESET_RESOURCES');
    }
    switch (state.mainResource) {
      case 'employee':
        await dispatch('initializeInternalEmployees', {
          refreshResources: refreshResources,
          refreshBlocks: refreshBlocks,
        });
        break;
      case 'external_employee':
        await dispatch('initializeExternalEmployees', {
          refreshResources: refreshResources,
          refreshBlocks: refreshBlocks,
        });
        break;
      case 'job':
        await dispatch('initializeJobs', {
          refreshBlocks: refreshBlocks,
        });
        break;
      case 'machine':
        await dispatch('initializeMachines', {
          refreshResources: refreshResources,
          refreshBlocks: refreshBlocks,
        });
        break;
      default:
        break;
    }
    commit('SET_LOADING_STATE', false);
  },
  async initializeBlocksOnly({ dispatch }) {
    await dispatch('initialize', {
      refreshResources: false,
      refreshBlocks: true,
    });
  },
  async initializeResourcesOnly({ dispatch }) {
    await dispatch('initialize', {
      refreshResources: true,
      refreshBlocks: false,
    });
  },
  async initializeInternalEmployees(
    { commit, dispatch },
    { refreshResources = true, refreshBlocks = true } = {}
  ) {
    commit('RESET_OVERLAPPING_ASSIGNMENT_IDS');
    if (refreshResources) {
      if (state.planningType === 'jobs') {
        Promise.all([
          dispatch('setResourcesInternalEmployees'),
          dispatch('getUserWorkShifts'),
        ]).then(arr => {
          dispatch('setUserWorkShifts', arr[1]);
        });
      } else {
        Promise.all([
          dispatch('setResourcesInternalEmployees'),
          dispatch('getUserWorkShifts'),
        ]).then(arr => {
          dispatch('setUserWorkShifts', arr[1]);
        });
      }
    }
    if (refreshBlocks) {
      commit('RESET_OVERLAPPING_ASSIGNMENT_IDS');
      if (state.planningType === 'jobs') {
        Promise.all([
          dispatch('getPlannedJobs'),
          dispatch('getUserWorkShifts'),
        ]).then(arr => {
          commit('plannedJobs', arr[0]);
          dispatch('setUserWorkShifts', arr[1]);
        });
      } else {
        Promise.all([
          dispatch('getPlannedUserAssignments'), //assignments
          dispatch('getLeaveRequests'), //extra
          dispatch('getSickRegistrations'), //extra
          dispatch('getPlanningWorkShiftBlocks'), //schedule
          dispatch('getUserWorkShifts'), //available time
        ]).then(arr => {
          commit('planningAssignments', arr[0]);
          commit('plannedExtraBlocks', concat(arr[1], arr[2]));
          commit('plannedScheduleBlocks', arr[3]);
          dispatch('setUserWorkShifts', arr[4]);
        });
      }
    }
  },
  async initializeExternalEmployees(
    { commit, dispatch },
    { refreshResources = true, refreshBlocks = true } = {}
  ) {
    if (refreshResources) {
      await dispatch('setResourcesExternalEmployees');
    }
    if (refreshBlocks) {
      if (state.planningType === 'jobs') {
        Promise.all([dispatch('getPlannedJobs')]).then(arr => {
          commit('plannedJobs', arr[0]);
        });
      } else {
        Promise.all([dispatch('getPlannedUserAssignments')]).then(arr => {
          commit('planningAssignments', arr[0]);
        });
      }
    }
  },
  async initializeMachines(
    { state, commit, dispatch },
    { refreshResources = true, refreshBlocks = true } = {}
  ) {
    commit('RESET_OVERLAPPING_ASSIGNMENT_IDS');
    if (refreshResources) {
      if (state.planningType === 'jobs') {
        Promise.all([
          dispatch('setResourcesItems'),
          dispatch('getMachineSchedules'),
        ]).then(arr => {
          dispatch('setMachineSchedules', arr[1]);
        });
      } else {
        Promise.all([
          dispatch('setResourcesMachines'),
          dispatch('getMachineSchedules'),
        ]).then(arr => {
          dispatch('setMachineSchedules', arr[1]);
        });
      }
    }
    if (refreshBlocks) {
      if (state.planningType === 'jobs') {
        Promise.all([
          dispatch('getPlannedJobs'),
          dispatch('getMachineSchedules'),
        ]).then(arr => {
          commit('plannedJobs', arr[0]);
          dispatch('setMachineSchedules', arr[1]);
        });
      } else {
        Promise.all([
          dispatch('getPlannedMachineAssignments'),
          dispatch('getRentalLines'),
          dispatch('getMachineSchedules'),
        ]).then(arr => {
          commit('planningAssignments', arr[0]);
          commit('plannedExtraBlocks', arr[1]);
          dispatch('setMachineSchedules', arr[2]);
        });
      }
    }
  },
  async initializeJobs({ commit, dispatch }, { refreshBlocks = true } = {}) {
    dispatch('setResourcesJobs');
    if (state.planningType === 'jobs') {
      commit('RESET_PLANNED_EXTRA_BLOCKS');
      commit('RESET_PLANNED_SCHEDULE_BLOCKS');
      if (refreshBlocks) {
        Promise.all([dispatch('getPlannedJobs')]).then(arr => {
          commit('plannedJobs', arr[0]);
        });
      }
    } else {
      if (refreshBlocks) {
        Promise.all([
          dispatch('getPlannedJobs'),
          dispatch('getPlanningJobScheduleBlocks'),
        ]).then(arr => {
          commit('plannedJobs', arr[0]);
          commit('plannedScheduleBlocks', arr[1]);
        });
      }
    }
  },
  setActiveObject({ commit, state }, object = null) {
    // if current block is already active, make inactive.
    if (isEqual(state.activeObject, object)) {
      // object = null;
    }
    commit('SET_ACTIVE_OBJECT', object);
  },
  setInterval({ commit, dispatch }, interval) {
    // naming collision with javascript setInterval()
    dispatch('resetActive');
    commit('interval', interval);
  },
  resetActive({ commit }) {
    commit('SET_ACTIVE_OBJECT', null);
  },
  setShowPlannedJobDropzone({ commit }, boolean) {
    commit('showPlannedJobDropzone', boolean);
  },
  setHighlighted({ commit }, object) {
    commit('highlighted', object);
  },
  setUserWorkShifts({ commit }, userWorkShifts) {
    commit('userWorkShifts', userWorkShifts);
    commit(
      'ADD_OVERLAPPING_ASSIGNMENT_IDS',
      flatMap(userWorkShifts, 'overlappingAssignmentIds')
    );
  },
  setMachineSchedules({ commit }, machineSchedules) {
    commit('machineSchedules', machineSchedules);
    commit(
      'ADD_OVERLAPPING_ASSIGNMENT_IDS',
      flatMap(machineSchedules, 'overlappingAssignmentIds')
    );
  },
  setShowAvailableUserSecondsRelative({ commit }, boolean) {
    commit('SET_SHOW_AVAILABLE_USER_SECONDS_RELATIVE', boolean);
  },
  setPlannedJobFilters({ commit }, filters={}) {
    commit('SET_PLANNED_JOB_FILTERS', filters)
  },
  setAssignmentFilters({ commit }, filters={}) {
    commit('SET_ASSIGNMENT_FILTERS', filters)
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
